import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, MARYLINE_AVATAR } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { toast } from "sonner";
import {
  Heart, Loader2, CheckCircle2, AlertTriangle, Scale, Compass, Route as RouteIcon,
  Sparkles, Eye, HelpCircle, MessageCircleHeart, Home, AlertCircle, ShieldAlert,
} from "lucide-react";

const asList = (v) => (Array.isArray(v) ? v : v ? [v] : []);
const item = (x) =>
  typeof x === "string" ? { title: "", detail: x } : { title: x?.title || "", detail: x?.detail || "" };

function Section({ icon: Icon, index, title, tint, children }) {
  return (
    <section data-testid={`result-section-${index}`}
      className="bg-white rounded-2xl border border-[#E8DDD5] p-6 sm:p-8 shadow-[0_4px_20px_-2px_rgba(110,45,56,0.06)] animate-fade-up">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: tint }}>
          <Icon className="w-5 h-5 text-[#6E2D38]" strokeWidth={1.75} />
        </div>
        <h2 className="font-editorial text-2xl font-semibold leading-tight">{title}</h2>
      </div>
      <div className="text-[15px] text-[#4A3B37] leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

function ItemList({ items, dot, dotBg }) {
  return (
    <ul className="space-y-3">
      {items.map((raw, i) => {
        const it = item(raw);
        return (
          <li key={i} className="flex gap-3">
            <span className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0" style={{ background: dot }} />
            <div>
              {it.title && <p className="font-semibold text-[#2B1E1B]">{it.title}</p>}
              <p>{it.detail}</p>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

const DYN_LABELS = {
  reciprocity: "Réciprocité", investment: "Investissement", communication: "Communication",
  consistency: "Cohérence paroles/actes", balance: "Équilibre", emotional_security: "Sécurité émotionnelle",
};

export default function Result() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | unpaid | error

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/analysis/${conversationId}`);
        setContent(data);
        setStatus("ok");
        trackEvent("result_viewed", conversationId);
      } catch (e) {
        const code = e?.response?.status;
        if (code === 402) setStatus("unpaid");
        else setStatus("error");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  if (status === "loading")
    return (
      <div className="min-h-[100dvh] flex flex-col items-center justify-center bg-[#FAF7F2] text-center px-5">
        <Loader2 className="w-8 h-8 animate-spin text-[#6E2D38] mb-4" />
        <p className="font-editorial text-2xl" data-testid="result-loading-title">Maryline prépare ton analyse…</p>
        <p className="text-[#5C4A45] text-sm mt-2">« Je rassemble les éléments importants de ton histoire. »</p>
        <p className="text-[#8C6B64] text-xs mt-3">Cela peut prendre quelques instants.</p>
      </div>
    );

  if (status !== "ok")
    return (
      <div className="min-h-[100dvh] flex flex-col items-center justify-center bg-[#FAF7F2] text-center px-5">
        <AlertCircle className="w-10 h-10 text-[#A83A3A] mb-4" />
        <h1 className="font-editorial text-2xl mb-2">
          {status === "unpaid" ? "Analyse non débloquée" : "Une erreur est survenue"}
        </h1>
        <p className="text-[#5C4A45] mb-6 max-w-sm">
          {status === "unpaid"
            ? "Cette analyse n'a pas encore été débloquée."
            : "Nous n'avons pas pu charger ton analyse. Réessaie dans un instant."}
        </p>
        <button onClick={() => navigate(status === "unpaid" ? `/chat?c=${conversationId}` : "/chat?new=1")}
          data-testid="result-back-to-chat"
          className="bg-[#6E2D38] hover:bg-[#54212A] text-white rounded-full px-6 py-3 font-medium">
          {status === "unpaid" ? "Retour à Maryline" : "Nouvelle analyse"}
        </button>
      </div>
    );

  const c = content || {};
  const compat = c.compatibility || {};
  const fvi = c.facts_vs_interpretations || {};
  const dyn = c.dynamics || {};
  const score = Math.max(0, Math.min(100, parseInt(compat.score) || 0));

  return (
    <div className="min-h-[100dvh] bg-[#FAF7F2] pb-16">
      {/* Header */}
      <header className="backdrop-blur-xl bg-[#FFFDF9]/90 border-b border-[#E8DDD5] sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-[#6E2D38] fill-[#6E2D38]" />
            <span className="font-editorial text-2xl font-semibold">RawLove</span>
          </div>
          <button onClick={() => navigate("/")} data-testid="result-home-button"
            className="p-2 rounded-full hover:bg-[#F2E9E1] transition-colors">
            <Home className="w-5 h-5 text-[#5C4A45]" />
          </button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-5 pt-10">
        {/* Intro */}
        <div className="text-center mb-10 animate-fade-up">
          <img src={MARYLINE_AVATAR} alt="Maryline"
            className="w-16 h-16 rounded-full object-cover mx-auto mb-4 border-2 border-white shadow-md" />
          <p className="text-xs font-sans tracking-widest uppercase text-[#8C6B64] font-semibold mb-2">
            Analyse confidentielle · préparée par Maryline
          </p>
          <h1 className="font-editorial text-4xl sm:text-5xl font-medium tracking-tight">Ton analyse RawLove</h1>
        </div>

        {c.safety && (
          <div className="bg-[#FDF0F0] border border-[#A83A3A]/25 rounded-2xl p-5 mb-6 flex gap-3" data-testid="result-safety">
            <ShieldAlert className="w-6 h-6 text-[#A83A3A] flex-shrink-0" />
            <p className="text-sm text-[#7a2b2b] leading-relaxed">{c.safety}</p>
          </div>
        )}

        <div className="space-y-5">
          <Section icon={Sparkles} index="understood" title="Ce que j'ai compris" tint="#F4E3E2">
            <p>{c.understood}</p>
          </Section>

          <Section icon={MessageCircleHeart} index="behaviors" title="Ce que ses comportements montrent" tint="#F4E3E2">
            <ItemList items={asList(c.behaviors)} dot="#6E2D38" />
          </Section>

          <Section icon={CheckCircle2} index="green-flags" title="Green Flags" tint="#EBF4EF">
            {asList(c.green_flags).length ? (
              <ItemList items={asList(c.green_flags)} dot="#2E6B4F" />
            ) : (
              <p className="text-[#8C6B64]">Aucun signal positif marquant identifié à ce stade.</p>
            )}
          </Section>

          <Section icon={AlertTriangle} index="red-flags" title="Red Flags" tint="#FDF0F0">
            {asList(c.red_flags).length ? (
              <ItemList items={asList(c.red_flags)} dot="#A83A3A" />
            ) : (
              <p className="text-[#8C6B64]">Aucun point de vigilance réellement soutenu par les faits fournis.</p>
            )}
          </Section>

          <Section icon={Scale} index="dynamics" title="Dynamique relationnelle" tint="#F2E9E1">
            <div className="grid sm:grid-cols-2 gap-4">
              {Object.keys(DYN_LABELS).map((k) =>
                dyn[k] ? (
                  <div key={k} className="bg-[#FAF7F2] rounded-xl p-4 border border-[#E8DDD5]">
                    <p className="text-xs font-semibold tracking-wide uppercase text-[#8C6B64] mb-1">{DYN_LABELS[k]}</p>
                    <p className="text-sm">{dyn[k]}</p>
                  </div>
                ) : null
              )}
            </div>
          </Section>

          <Section icon={Compass} index="compatibility" title="Compatibilité" tint="#F4E3E2">
            <div className="flex items-center gap-5 mb-4">
              <div className="relative w-24 h-24 flex-shrink-0">
                <svg viewBox="0 0 100 100" className="w-24 h-24 -rotate-90">
                  <circle cx="50" cy="50" r="42" fill="none" stroke="#F2E9E1" strokeWidth="10" />
                  <circle cx="50" cy="50" r="42" fill="none" stroke="#6E2D38" strokeWidth="10"
                    strokeLinecap="round" strokeDasharray={`${(score / 100) * 264} 264`} />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="font-editorial text-2xl font-semibold">{score}%</span>
                </div>
              </div>
              <div>
                <p className="font-semibold text-lg">{compat.label}</p>
                <p className="text-sm text-[#8C6B64] italic mt-1">{compat.caveat}</p>
              </div>
            </div>
            {asList(compat.factors).length > 0 && (
              <div>
                <p className="font-semibold mb-2 text-[#2B1E1B]">Facteurs qui influencent cet indicateur</p>
                <ItemList items={asList(compat.factors)} dot="#C48B92" />
              </div>
            )}
          </Section>

          <Section icon={Eye} index="facts" title="Faits vs interprétations" tint="#F2E9E1">
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                ["Ce que l'on sait", fvi.facts, "#2E6B4F"],
                ["Ce que tu ressens", fvi.feelings, "#C48B92"],
                ["Ce que tu interprètes", fvi.interpretations, "#6E2D38"],
                ["Ce qu'on ne peut pas déterminer", fvi.unknowns, "#8C6B64"],
              ].map(([label, arr, dot]) => (
                <div key={label} className="bg-[#FAF7F2] rounded-xl p-4 border border-[#E8DDD5]">
                  <p className="text-xs font-semibold tracking-wide uppercase mb-2" style={{ color: dot }}>{label}</p>
                  {asList(arr).length ? (
                    <ItemList items={asList(arr)} dot={dot} />
                  ) : (
                    <p className="text-sm text-[#8C6B64]">—</p>
                  )}
                </div>
              ))}
            </div>
          </Section>

          <Section icon={HelpCircle} index="central-answer" title="Réponse à ta question" tint="#F4E3E2">
            <p className="text-[16px]">{c.central_answer}</p>
          </Section>

          <Section icon={RouteIcon} index="action-plan" title="Ton plan d'action" tint="#EBF4EF">
            <ol className="space-y-4">
              {asList(c.action_plan).map((raw, i) => {
                const it = item(raw);
                return (
                  <li key={i} className="flex gap-4">
                    <span className="font-editorial text-2xl font-semibold text-[#C48B92] leading-none w-8 flex-shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div>
                      {it.title && <p className="font-semibold text-[#2B1E1B]">{it.title}</p>}
                      <p>{it.detail}</p>
                    </div>
                  </li>
                );
              })}
            </ol>
          </Section>
        </div>

        <div className="mt-10 text-center">
          <p className="text-sm text-[#8C6B64] mb-4 flex items-center justify-center gap-2">
            <Heart className="w-4 h-4 text-[#6E2D38]" />
            Prends soin de toi. Tu es la mieux placée pour décider de la suite.
          </p>
          <button
            onClick={() => { navigator.clipboard?.writeText(window.location.href); toast.success("Lien copié"); }}
            data-testid="result-copy-link"
            className="bg-[#F4E3E2] hover:bg-[#EBDAD8] text-[#6E2D38] border border-[#C48B92]/30 rounded-full px-6 py-3 font-medium transition-all">
            Copier le lien de mon analyse
          </button>
          <div className="mt-4">
            <button onClick={() => navigate("/chat?new=1")} data-testid="result-new-analysis-button"
              className="text-sm text-[#6E2D38] underline underline-offset-4 hover:text-[#54212A] transition-colors">
              Commencer une nouvelle analyse
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
